import { ALL_WIDGET_IDS } from "app/pages/sales-intelligence/modules/extension-management/constants";
import type {
    OverviewWidgetId,
    OverviewWidgetItem,
} from "app/pages/sales-intelligence/modules/extension-management/types";
import CompanyIndustryType from "app/pages/sales-intelligence/types/industries";

export type { OverviewWidgetId, OverviewWidgetItem };

export type OverviewWidgetDef = {
    id: OverviewWidgetId;
    titleKey: string;
    descriptionKey: string;
    exampleType: "image" | "text";
    exampleContent: string;
};

export const FIXED_OVERVIEW_WIDGET_DEF: Omit<OverviewWidgetDef, "id"> = {
    titleKey: "si.management.extension.settings.widget.overview.title",
    descriptionKey: "si.management.extension.settings.widget.overview.description",
    exampleType: "image",
    exampleContent: "/images/sales-intelligence/widget-overview-example.png",
};

export const OVERVIEW_WIDGET_DEFS: OverviewWidgetDef[] = [
    {
        id: "firmographics",
        titleKey: "si.management.extension.settings.widget.firmographics.title",
        descriptionKey: "si.management.extension.settings.widget.firmographics.description",
        exampleType: "image",
        exampleContent: "/images/sales-intelligence/widget-companydata-example.png",
    },
    {
        id: "signals",
        titleKey: "si.management.extension.settings.widget.signals.title",
        descriptionKey: "si.management.extension.settings.widget.signals.description",
        exampleType: "image",
        exampleContent: "/images/sales-intelligence/widget-signals-example.png",
    },
    {
        id: "salesforceActivity",
        titleKey: "si.management.extension.settings.widget.salesforce_activity.title",
        descriptionKey: "si.management.extension.settings.widget.salesforce_activity.description",
        exampleType: "image",
        exampleContent: "/images/sales-intelligence/widget-salesforceactivity-example.png",
    },
    {
        id: "trafficVisits",
        titleKey: "si.management.extension.settings.widget.traffic_visits.title",
        descriptionKey: "si.management.extension.settings.widget.traffic_visits.description",
        exampleType: "image",
        exampleContent: "/images/sales-intelligence/widget-trafficvisits-example.png",
    },
    {
        id: "trafficShare",
        titleKey: "si.management.extension.settings.widget.traffic_share.title",
        descriptionKey: "si.management.extension.settings.widget.traffic_share.description",
        exampleType: "image",
        exampleContent: "/images/sales-intelligence/widget-trafficshare-example.png",
    },
    {
        id: "technologies",
        titleKey: "si.management.extension.settings.widget.technologies.title",
        descriptionKey: "si.management.extension.settings.widget.technologies.description",
        exampleType: "image",
        exampleContent: "/images/sales-intelligence/widget-technologies-example.png",
    },
    {
        id: "engagement",
        titleKey: "si.management.extension.settings.widget.engagement.title",
        descriptionKey: "si.management.extension.settings.widget.engagement.description",
        exampleType: "image",
        exampleContent: "/images/sales-intelligence/widget-engagement-example.png",
    },
    {
        id: "adNetworks",
        titleKey: "si.management.extension.settings.widget.ad_networks.title",
        descriptionKey: "si.management.extension.settings.widget.ad_networks.description",
        exampleType: "image",
        exampleContent: "/images/sales-intelligence/widget-adnetworks-example.png",
    },
    {
        id: "ecommerce",
        titleKey: "si.management.extension.settings.widget.ecommerce.title",
        descriptionKey: "si.management.extension.settings.widget.ecommerce.description",
        exampleType: "image",
        exampleContent: "/images/sales-intelligence/widget-ecommerce-example.png",
    },
];

const DEFAULT_WIDGET_IDS: OverviewWidgetId[] = [
    "firmographics",
    "signals",
    "salesforceActivity",
    "trafficVisits",
    "engagement",
    "trafficShare",
    "ecommerce",
    "technologies",
];

const INDUSTRY_OVERVIEW_WIDGETS_MAP: Partial<Record<CompanyIndustryType, OverviewWidgetId[]>> = {
    [CompanyIndustryType.AdTechAdNetwork]: [
        "firmographics",
        "signals",
        "salesforceActivity",
        "trafficVisits",
        "engagement",
        "trafficShare",
        "adNetworks",
        "technologies",
    ],
    [CompanyIndustryType.ConsultingMarketResearch]: [
        "firmographics",
        "signals",
        "salesforceActivity",
        "trafficVisits",
        "engagement",
        "trafficShare",
        "adNetworks",
    ],
    [CompanyIndustryType.B2BSoftware]: [
        "firmographics",
        "signals",
        "salesforceActivity",
        "trafficVisits",
        "ecommerce",
        "technologies",
        "engagement",
    ],
    [CompanyIndustryType.Payments]: [
        "firmographics",
        "signals",
        "salesforceActivity",
        "ecommerce",
        "trafficVisits",
        "trafficShare",
        "technologies",
        "engagement",
    ],
    [CompanyIndustryType.LogisticsAndShipping]: [
        "firmographics",
        "signals",
        "salesforceActivity",
        "ecommerce",
        "trafficVisits",
        "trafficShare",
        "technologies",
        "engagement",
    ],
    [CompanyIndustryType.Agency]: [
        "firmographics",
        "signals",
        "salesforceActivity",
        "trafficVisits",
        "engagement",
        "trafficShare",
        "adNetworks",
        "technologies",
    ],
    [CompanyIndustryType.PrivateEquityAndVentureCapital]: [
        "firmographics",
        "signals",
        "salesforceActivity",
        "trafficVisits",
        "engagement",
        "trafficShare",
        "technologies",
    ],
    [CompanyIndustryType.Investors]: [
        "firmographics",
        "signals",
        "salesforceActivity",
        "trafficVisits",
        "engagement",
        "trafficShare",
        "technologies",
    ],
};

export const getDefaultOverviewWidgets = (industry?: CompanyIndustryType | null): OverviewWidgetItem[] => {
    const enabledIds =
        industry && industry in INDUSTRY_OVERVIEW_WIDGETS_MAP
            ? INDUSTRY_OVERVIEW_WIDGETS_MAP[industry]!
            : DEFAULT_WIDGET_IDS;

    const enabledSet = new Set(enabledIds);
    const uncheckedIds = ALL_WIDGET_IDS.filter((id) => !enabledSet.has(id));

    return [...enabledIds, ...uncheckedIds].map((id) => ({
        id,
        isChecked: enabledSet.has(id),
    }));
};

export const OVERVIEW_WIDGET_DEF_MAP = new Map(OVERVIEW_WIDGET_DEFS.map((def) => [def.id, def]));
